import { toast } from '@/components/toast';
import { AGENT_DEFAULT_NAME } from '@/pages/agents/constants';
import { createAgent, updateAgent } from '@/services/api';
import { logger } from '@/services/logger';
import { AgentUseCase, IVoice, Presenter } from '@d-id/types';
import axios, { AxiosError } from 'axios';
import { useMutation } from 'react-query';
import { enrichPresenter } from '../utils';

interface createUpdateAgents {
    agentId?: string;
    presenter: Presenter;
    voice: IVoice;
    isStitch?: boolean;
    controller?: AbortController;
    campaignId: string;
}
const RETRIES = 3;

export const useSaveCampaignAgent = () => {
    const mutationFn = ({ agentId, presenter: rawPresenter, voice, isStitch, controller, campaignId }: createUpdateAgents) => {
        const presenter = enrichPresenter({ presenter: rawPresenter, voice, isStitch });
        const payload = {
            preview_name: AGENT_DEFAULT_NAME,
            use_case: AgentUseCase.CAMPAIGNS,
            access: 'unlisted',
            presenter,
            user_data: JSON.stringify({ campaign_id: campaignId }),
            triggers: undefined,
        };

        if (agentId) {
            return updateAgent({ agentId, update: payload, controller });
        }

        return createAgent(payload, controller);
    };

    return useMutation('create-campaign-agent', mutationFn, {
        retry(failureCount, error: AxiosError) {
            const axiosError = error as AxiosError;

            return (
                !axios.isCancel(error) &&
                failureCount < RETRIES &&
                !!axiosError.response?.status &&
                (axiosError.response.status === 403 || axiosError.response.status === 401 || axiosError.response.status >= 500)
            );
        },
        onError(error: AxiosError<{ kind: string; description?: string; details?: { terms: string[] } }>) {
            if (axios.isCancel(error)) {
                return;
            }
            logger.error('Error creating agent for campaigns', error);
            toast.error({ title: 'Failed to create your campaign avatar, please try again!', toastId: 'create-campaign-agent' });
        },
    });
};
